/**
 * ReactWindowGrid: Highly-performant, virtualized React data grid for large datasets (1M+ rows).
 *
 * Features:
 * - Virtualization using react-window VariableSizeList for efficient rendering.
 * - Custom cell rendering with ellipsis and tooltip for overflowing text.
 * - Scroll-to-row logic for selectedElementId (centers row) and firstVisibleElementFromTop (scrolls row to top).
 * - Persistent row highlighting and parent integration for row selection and data extraction.
 * - Visually hidden scrollbars with full scroll functionality.
 * - Responsive height: grid fills available space as determined by parent container.
 * - Prevents cell content from overlapping other cells.
 * - Memoized for performance (using React.memo).
 *
 * Usage example:
 *   const columns = useMemo(() => [...], []);
 *   const data = useMemo(() => [...], [rowCount]);
 *   <ReactWindowGrid
 *     columns={columns}
 *     data={data}
 *     selectedElementId={selectedId}
 *     firstVisibleElementFromTop={firstId}
 *     onRowDataClick={handleRowClick}
 *     ...otherProps
 *   />
 */

import React, { Component, createRef, memo } from 'react';
import { VariableSizeList as List } from 'react-window';
import { isNullOrUndefined } from 'utils/object-utils';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

const REACT_WINDOW_GRID = 'react-window-grid';
const TRUNCATED_ELLIPSIS_CELL = 'truncating-ellipsis-grid-cell';
const ROW_HEIGHT = 48;
const HEIGHT_FROM_TOP_HEADER = 256;
const DEFAULT_COLUMN_WIDTH = 100;
// DataGridColumnsHeader: Renders the header row with column labels and widths
export const DataGridColumnsHeader = ({ columns }) => {
  // Build gridTemplateColumns string from column widths
  const gridTemplateColumns = columns
    .map((col) => `${parseInt(col.width)}px`)
    .join(' ');
  return (
    <div
      className={`${REACT_WINDOW_GRID}__header`}
      style={{
        gridTemplateColumns,
        width: columns.reduce((sum, col) => sum + (col.width || 100), 0),
      }}
    >
      {columns.map((col, idx) => {
        return (
          <div
            key={col.key || idx}
            className={classNames(`${REACT_WINDOW_GRID}__header-cell`, {
              [col.className]: col.className,
            })}
            style={{
              minWidth: col.width,
              maxWidth: col.width,
              width: col.width,
            }}
          >
            <span>
              <FormattedMessage id={col.header} />
            </span>
          </div>
        );
      })}
    </div>
  );
};

class ReactWindowGrid extends Component {
  // Initializes columns, data, and sets up refs and initial state
  constructor(props) {
    super(props);
    const columnsRaw = props.columns || [];
    const columns = columnsRaw.map((col) => ({
      ...col,
      width: parseInt(col.width),
    }));
    const data =
      props.data && Array.isArray(props.data)
        ? props.data.map((row, i) => ({
            ...row,
            id: row.id ?? row._id ?? row.key ?? `row-${i + 1}`,
          }))
        : [];
    this.state = {
      columnCount: columns.length,
      rowCount: data.length,
      width: columns.reduce(
        (sum, col) => sum + (parseInt(col.width) || 100),
        0,
      ),
      rowHeight: ROW_HEIGHT,
      columns,
      data,
      gridHeight: this.getInitialGridHeight(),
    };
    this.listRef = createRef();
    this.headerScrollRef = createRef();
  }

  // Calculates initial grid height based on window size (minus toolbars/header)
  getInitialGridHeight = () => window.innerHeight - HEIGHT_FROM_TOP_HEADER;

  // Updates grid height on window resize
  updateGridHeight = () =>
    this.setState({ gridHeight: this.getInitialGridHeight() });

  // Adds/removes window resize event listener
  componentDidMount() {
    window.addEventListener('resize', this.updateGridHeight);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.updateGridHeight);
  }

  // Scrolls to and highlights the selected row if selectedElementId changes,
  // or scrolls firstVisibleElementFromTop to top if selectedElementId is null
  componentDidUpdate(prevProps) {
    const {
      selectedElementId,
      firstVisibleElementFromTop,
      data = [],
      primaryKeyColumn = 'id',
    } = this.props;
    // If selectedElementId is set, scroll to and highlight that row (centered)
    if (selectedElementId) {
      const rowIndex = data.findIndex(
        (row, i) =>
          (row[primaryKeyColumn] ??
            row.id ??
            row._id ??
            row.key ??
            `row-${i}`) === selectedElementId,
      );
      if (rowIndex !== -1 && this.listRef.current) {
        this.listRef.current.scrollToItem(rowIndex, 'center');
      }
    } else if (
      firstVisibleElementFromTop &&
      firstVisibleElementFromTop !== prevProps.firstVisibleElementFromTop
    ) {
      // If selectedElementId is null, scroll the firstVisibleElementFromTop to the top
      const rowIndex = data.findIndex(
        (row, i) =>
          (row[primaryKeyColumn] ??
            row.id ??
            row._id ??
            row.key ??
            `row-${i}`) === firstVisibleElementFromTop,
      );
      if (rowIndex !== -1 && this.listRef.current) {
        this.listRef.current.scrollToItem(rowIndex, 'start');
      }
    }
  }

  // Returns the height of each row (fixed)
  getRowHeight = () => this.state.rowHeight;

  // Returns the width of a column at a given index
  getColumnWidth = (index) => {
    const { columns = [] } = this.props;
    const col = columns[index];
    return col && col.width ? parseInt(col.width) : DEFAULT_COLUMN_WIDTH;
  };

  // Synchronizes the header's horizontal scroll position with the grid's scroll position
  onGridScroll = ({ scrollLeft }) => {
    if (this.headerScrollRef.current) {
      this.headerScrollRef.current.scrollLeft = scrollLeft;
    }
  };

  // Renders a single cell in the grid, with ellipsis/tooltip for overflow
  renderCell = ({ columnIndex, rowIndex, style, className }) => {
    const {
      columns = [],
      data = [],
      primaryKeyColumn = 'id',
      onRowDataClick,
    } = this.props;
    if (!data[rowIndex]) return null;
    const rowData = data[rowIndex];
    const column = columns[columnIndex];
    let cellValue;
    if (typeof column.formatter === 'function') {
      cellValue = column.formatter(rowData, {
        rowKey: rowData[primaryKeyColumn] ?? rowIndex,
        rowIndex,
        column,
      });
    } else {
      cellValue = rowData[column.key];
    }
    const isStringOrNumber =
      typeof cellValue === 'string' || typeof cellValue === 'number';
    if (isStringOrNumber && String(cellValue).trim() !== '') {
      cellValue = <TruncatedEllipsisCell value={String(cellValue)} />;
    } else if (isNullOrUndefined(cellValue) || cellValue === '') {
      cellValue = '';
    }
    // Add onClick to cell to trigger onRowDataClick with rowData
    const handleCellClick = onRowDataClick
      ? () => onRowDataClick(rowData)
      : undefined;
    return (
      <div
        key={`${rowData[primaryKeyColumn]}-${column.key}`}
        className={classNames(`${REACT_WINDOW_GRID}__row-cell`, className, {
          [column.className]: column.className,
        })}
        style={{
          ...style,
          width: column.width,
          maxWidth: column.width,
        }}
        tabIndex={0}
        data-column-key={column.key}
        data-row-index={rowIndex}
        onClick={handleCellClick}
      >
        {cellValue}
      </div>
    );
  };

  // Renders a single row in the grid, highlights if selected, and handles row click
  renderRow = ({ index, style }) => {
    const {
      columns = [],
      data = [],
      primaryKeyColumn = 'id',
      selectedElementId,
      onRowDataClick,
      rowClassnames,
    } = this.props;
    const rowData = data[index];
    if (!rowData) return null;
    const rowId = rowData[primaryKeyColumn] ?? `row-${index}`;
    const isHighlighted = selectedElementId && rowId === selectedElementId;
    const handleRowClick = onRowDataClick
      ? () => onRowDataClick(rowData)
      : undefined;
    return (
      <div
        id={rowId}
        key={rowId}
        className={classNames(`${REACT_WINDOW_GRID}__row`, {
          [rowClassnames]: rowClassnames,
          [`${REACT_WINDOW_GRID}__row--highlighted`]: isHighlighted,
        })}
        style={{
          ...style,
          gridTemplateColumns: columns
            .map((col) => `${parseInt(col.width)}px`)
            .join(' '),
        }}
        data-row-index={index}
        onClick={handleRowClick}
        tabIndex={0}
        role="row"
      >
        {columns.map((column, columnIndex) =>
          this.renderCell({
            columnIndex,
            rowIndex: index,
            style: {
              width: column.width,
              minWidth: column.width,
              maxWidth: column.width,
            },
          }),
        )}
      </div>
    );
  };

  // Renders the entire grid UI, including header and virtualized list
  render() {
    const { columns = [], data = [], NoResultsComponent } = this.props;
    const width = columns.reduce(
      (sum, col) => sum + (parseInt(col.width) || DEFAULT_COLUMN_WIDTH),
      0,
    );
    return (
      <div className={`${REACT_WINDOW_GRID}--main`}>
        <div
          className={`${REACT_WINDOW_GRID}--container`}
          ref={this.headerScrollRef}
        >
          <div
            className={`${REACT_WINDOW_GRID}--header`}
            style={{
              minWidth: width,
              gridTemplateColumns: columns.map((col) => col.width).join(' '),
            }}
          >
            <DataGridColumnsHeader columns={columns} />
          </div>
          {data.length === 0 && NoResultsComponent ? (
            <div className={`${REACT_WINDOW_GRID}--no-results`} style={{}}>
              {NoResultsComponent}
            </div>
          ) : (
            <List
              height={this.state.gridHeight}
              className={classNames(
                `${REACT_WINDOW_GRID}--list`,
                'hide-scrollbar',
              )}
              itemCount={data.length}
              itemSize={this.getRowHeight}
              width={width}
              ref={this.listRef}
              style={{
                minWidth: width,
              }}
            >
              {this.renderRow}
            </List>
          )}
        </div>
      </div>
    );
  }
}

// Add defaultProps for optional props
ReactWindowGrid.defaultProps = {
  NoResultsComponent: null,
  useVirtualizedList: true,
  onRowCheckboxSelect: null,
  renderCell: null,
  rowClassnames: '',
  primaryKeyColumn: 'id',
  shouldDisableRow: null,
  onDropdownColumnSelect: null,
  highlightedRowIndex: undefined,
};

ReactWindowGrid.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      formatter: PropTypes.func,
      className: PropTypes.string,
    }),
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  NoResultsComponent: PropTypes.node,
  useVirtualizedList: PropTypes.bool,
  onRowCheckboxSelect: PropTypes.func,
  renderCell: PropTypes.func,
  rowClassnames: PropTypes.string,
  primaryKeyColumn: PropTypes.string,
  shouldDisableRow: PropTypes.func,
  onDropdownColumnSelect: PropTypes.func,
  highlightedRowIndex: PropTypes.number,
  selectedElementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  firstVisibleElementFromTop: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onRowDataClick: PropTypes.func,
  rowCount: PropTypes.number,
};

export default memo(ReactWindowGrid);

// Utility: Only show tooltip if text is truncated
// TruncatedEllipsisCell: Shows cell value with ellipsis and tooltip if truncated
export const TruncatedEllipsisCell = ({ value }) => {
  const spanRef = React.useRef(null);
  const [isTruncated, setIsTruncated] = React.useState(false);
  const THRESHOLD_VALUE = 30;
  const checkTruncation = React.useCallback(() => {
    const el = spanRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  React.useEffect(() => {
    // Defer measurement to after paint and after layout
    const timeout = setTimeout(() => {
      requestAnimationFrame(checkTruncation);
    }, 0);
    window.addEventListener('resize', checkTruncation);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [value, checkTruncation]);

  // Fallback: if value is long, always show tooltip
  const showTooltip =
    isTruncated ||
    (typeof value === 'string' && value.length > THRESHOLD_VALUE);

  const getLeftAndTopValue = () => {
    if (!spanRef.current) return { left: 0, top: 0 };
    const rect = spanRef.current.getBoundingClientRect();
    // tooltipWidth: Maximum width for the tooltip (60% of viewport width) to prevent overflow
    // tooltipHeight: Estimated height for the tooltip, used to calculate vertical positioning
    const tooltipWidth = window.innerWidth * 0.6;
    const tooltipHeight = 80; // estimate

    // Calculate left position
    let toolTipLeft;
    if (rect.left + tooltipWidth > window.innerWidth) {
      toolTipLeft = Math.max(window.innerWidth - tooltipWidth - 16, 8);
    } else {
      toolTipLeft = rect.left;
    }

    // Calculate top position
    let toolTipTop;
    if (rect.bottom + tooltipHeight > window.innerHeight) {
      toolTipTop = Math.max(rect.top - tooltipHeight - 8, 8);
    } else {
      toolTipTop = rect.bottom + 4;
    }

    return { toolTipLeft, toolTipTop };
  };

  const { toolTipLeft, toolTipTop } = getLeftAndTopValue();
  // Show tooltip with full value if truncated or overflowing
  return (
    <div
      className={`${TRUNCATED_ELLIPSIS_CELL}`}
      title={showTooltip ? value : undefined}
    >
      <span className={`${TRUNCATED_ELLIPSIS_CELL}--no-tooltip`} ref={spanRef}>
        {value}
      </span>
      {showTooltip && (
        <span
          className={`${TRUNCATED_ELLIPSIS_CELL}--tooltip`}
          style={{
            left: toolTipLeft,
            top: toolTipTop,
          }}
        >
          {value}
        </span>
      )}
    </div>
  );
};
